import {
  Alert,
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { listAccounts, listFiles, sendOperation, submitFile } from '../../api/endpoints';
import { getValidationFailures } from '../../api/http';
import { queryKeys } from '../../api/queryKeys';
import type { FileListFilter, SendOperation, StoredFileListItem } from '../../api/types';
import { ErrorBlock } from '../../components/QueryStateOverlay';
import { ValidationFindingsDialog, type ValidationFindings } from '../../components/ValidationFindingsDialog';
import { useErrorToast, useSuccessToast } from '../../hooks/useErrorToast';
import { useSelectedTenant } from '../../tenant/tenantSelection';
import { accountLabel, formatBytes } from '../../utils/format';
import { sha256Hex } from '../../utils/hash';

const documentFilter: FileListFilter = {
  page: 1,
  pageSize: 200,
  kinds: ['BisDocument', 'IsdocDocument'],
  sortBy: 'createdAt',
  sortDirection: 'desc',
};

const attachmentFilter: FileListFilter = {
  page: 1,
  pageSize: 200,
  kinds: ['Attachment'],
  sortBy: 'createdAt',
  sortDirection: 'desc',
};

/** Where the document comes from: a file already stored here, or one picked from disk right now. */
type DocumentSource = 'stored' | 'upload';
type DocumentKind = 'BisDocument' | 'IsdocDocument';

const externalDocumentIdMaxLength = 64;

export interface SendDialogProps {
  onClose: () => void;
  /**
   * Locks the dialog to the upload-from-disk source. The submit page uses it for a bare
   * `registration` submitter, whose role reaches the one-call submit but not the stored files the
   * other source picks from.
   */
  uploadOnly?: boolean;
}

export function SendDialog({ onClose, uploadOnly = false }: SendDialogProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toastError = useErrorToast();
  const toastSuccess = useSuccessToast();
  const { selectedTenantGid } = useSelectedTenant();

  const [source, setSource] = useState<DocumentSource>(uploadOnly ? 'upload' : 'stored');
  const [accountGid, setAccountGid] = useState('');
  const [externalDocumentId, setExternalDocumentId] = useState('');

  const [documentFile, setDocumentFile] = useState<StoredFileListItem | null>(null);
  const [attachments, setAttachments] = useState<StoredFileListItem[]>([]);

  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadKind, setUploadKind] = useState<DocumentKind>('BisDocument');
  const [uploadAttachments, setUploadAttachments] = useState<File[]>([]);
  // One key per dialog opening, not per attempt: a retry after a timeout must be recognised as the
  // same submission and answered with the first operation rather than sending twice.
  const [idempotencyKey] = useState<string>(() => crypto.randomUUID());

  // What validation said, and where to continue once the findings are dismissed: a warning means
  // the submission is in, so closing the report navigates to the operation it created.
  const [findings, setFindings] = useState<ValidationFindings | null>(null);
  const [operationAfterFindings, setOperationAfterFindings] = useState<string | null>(null);

  const accountsQuery = useQuery({
    queryKey: queryKeys.accounts.list(selectedTenantGid),
    queryFn: ({ signal }) => listAccounts(signal),
    enabled: selectedTenantGid !== null,
  });

  const documentsQuery = useQuery({
    queryKey: queryKeys.files.list(selectedTenantGid, documentFilter),
    queryFn: ({ signal }) => listFiles(documentFilter, signal),
    enabled: selectedTenantGid !== null && source === 'stored',
  });

  const attachmentsQuery = useQuery({
    queryKey: queryKeys.files.list(selectedTenantGid, attachmentFilter),
    queryFn: ({ signal }) => listFiles(attachmentFilter, signal),
    enabled: selectedTenantGid !== null && source === 'stored',
  });

  const documents = documentsQuery.data?.items ?? [];
  const attachmentOptions = attachmentsQuery.data?.items ?? [];
  const selectedAccount = (accountsQuery.data ?? []).find((account) => account.accountGid === accountGid);

  const chooseUploadFile = (file: File | null) => {
    setUploadFile(file);
    // The extension is only a first guess; the radio stays in charge.
    if (file) setUploadKind(/\.isdoc[x]?$/i.test(file.name) ? 'IsdocDocument' : 'BisDocument');
  };

  const mutation = useMutation({
    mutationFn: async (): Promise<{
      operation: SendOperation | null;
      warnings: ValidationFindings['items'];
    }> => {
      const participantId = selectedAccount?.participantId ?? '';
      const documentId = externalDocumentId.trim() || undefined;

      if (source === 'stored') {
        const operation = await sendOperation(participantId, {
          documentFileGid: documentFile?.fileGid ?? '',
          attachmentFileGids: attachments.map((file) => file.fileGid),
          externalDocumentId: documentId,
        });
        return { operation, warnings: [] };
      }

      if (!uploadFile) throw new Error(t('files.upload.chooseFirst'));

      const result = await submitFile({
        participantId,
        file: uploadFile,
        kind: uploadKind,
        attachments: uploadAttachments,
        externalDocumentId: documentId,
        sha256: await sha256Hex(uploadFile),
        idempotencyKey,
      });
      return { operation: result.operation, warnings: result.validation };
    },
    onSuccess: async ({ operation, warnings }) => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.operations.all });
      await queryClient.invalidateQueries({ queryKey: queryKeys.files.all });

      if (operation?.duplicate) toastSuccess(t('operations.send.duplicate'));

      if (operation && warnings.length > 0) {
        // Closing on top of the findings would be the only chance to read them, gone.
        setOperationAfterFindings(operation.operationGid);
        setFindings({ severity: 'warning', message: t('operations.send.sentWithWarnings'), items: warnings });
        return;
      }

      onClose();
      // The relay delivers out of band, so the detail route is where the outcome actually arrives.
      if (operation) void navigate(`/operations/${operation.operationGid}`);
    },
    onError: (error) => {
      const found = getValidationFailures(error);
      // A rejection stored nothing — the form stays open to fix the file and try again.
      if (found.length > 0)
        setFindings({ severity: 'error', message: t('operations.send.rejected'), items: found });
      else toastError(error);
    },
  });

  // The account is addressed by its Peppol participant identifier, so one the gate has not given an
  // address to yet cannot send at all — refreshing its identifiers from Flowis is what fixes that.
  const documentChosen = source === 'stored' ? documentFile !== null : uploadFile !== null;
  const canSend = documentChosen && !!selectedAccount?.participantId && !mutation.isPending;

  return (
    <>
      <Dialog open onClose={mutation.isPending ? undefined : onClose} maxWidth="sm" fullWidth>
        <DialogTitle>{t('operations.send.title')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            <TextField
              select
              label={t('operations.send.fromAccount')}
              value={accountGid}
              onChange={(event) => setAccountGid(event.target.value)}
              required
              helperText={accountsQuery.isError ? t('operations.send.accountsFailed') : undefined}
              error={accountsQuery.isError}
            >
              {(accountsQuery.data ?? []).map((account) => (
                <MenuItem
                  key={account.accountGid}
                  value={account.accountGid}
                  disabled={!account.enabled || !account.participantId}
                >
                  {accountLabel(account)}
                  {!account.enabled && t('operations.send.disabledSuffix')}
                  {account.enabled && !account.participantId && t('operations.send.noParticipantSuffix')}
                </MenuItem>
              ))}
            </TextField>

            {selectedAccount && !selectedAccount.participantId && (
              <Alert severity="warning">{t('operations.send.noParticipant')}</Alert>
            )}

            {!uploadOnly && (
              <FormControl>
                <FormLabel>{t('operations.send.source')}</FormLabel>
                <RadioGroup
                  row
                  value={source}
                  onChange={(event) => setSource(event.target.value as DocumentSource)}
                >
                  <FormControlLabel
                    value="stored"
                    control={<Radio />}
                    label={t('operations.send.sourceStored')}
                  />
                  <FormControlLabel
                    value="upload"
                    control={<Radio />}
                    label={t('operations.send.sourceUpload')}
                  />
                </RadioGroup>
              </FormControl>
            )}

            {source === 'stored' ? (
              <>
                {documentsQuery.isError && (
                  <ErrorBlock error={documentsQuery.error} onRetry={() => void documentsQuery.refetch()} />
                )}

                <Autocomplete
                  options={documents}
                  loading={documentsQuery.isPending}
                  value={documentFile}
                  onChange={(_event, value) => setDocumentFile(value)}
                  getOptionLabel={(option) => option.fileName}
                  isOptionEqualToValue={(option, value) => option.fileGid === value.fileGid}
                  renderOption={(props, option) => {
                    const { key, ...rest } = props;
                    return (
                      <li key={key} {...rest}>
                        <Stack>
                          <Typography variant="body2">{option.fileName}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {option.kind === 'IsdocDocument' ? 'ISDOC' : 'Peppol BIS'} ·{' '}
                            {formatBytes(option.sizeBytes)}
                          </Typography>
                        </Stack>
                      </li>
                    );
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label={t('operations.send.document')} required />
                  )}
                />

                {documentFile?.kind === 'IsdocDocument' && (
                  <Alert severity="info">{t('operations.send.isdocNote')}</Alert>
                )}

                {attachmentsQuery.isError && (
                  <ErrorBlock
                    error={attachmentsQuery.error}
                    onRetry={() => void attachmentsQuery.refetch()}
                  />
                )}

                <Autocomplete
                  multiple
                  options={attachmentOptions}
                  loading={attachmentsQuery.isPending}
                  value={attachments}
                  onChange={(_event, value) => setAttachments([...value])}
                  getOptionLabel={(option) => option.fileName}
                  isOptionEqualToValue={(option, value) => option.fileGid === value.fileGid}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('operations.send.attachments')}
                      helperText={t('operations.send.attachmentsHelp')}
                    />
                  )}
                />
              </>
            ) : (
              <>
                <Button variant="outlined" component="label">
                  {uploadFile
                    ? t('files.upload.selected', { name: uploadFile.name })
                    : t('files.upload.chooseFile')}
                  <input
                    type="file"
                    hidden
                    accept=".xml,.isdoc,text/xml,application/xml"
                    onChange={(event) => chooseUploadFile(event.target.files?.[0] ?? null)}
                  />
                </Button>

                <FormControl>
                  <FormLabel>{t('files.upload.format')}</FormLabel>
                  <RadioGroup
                    row
                    value={uploadKind}
                    onChange={(event) => setUploadKind(event.target.value as DocumentKind)}
                  >
                    <FormControlLabel
                      value="BisDocument"
                      control={<Radio />}
                      label={t('fileKinds.BisDocument')}
                    />
                    <FormControlLabel
                      value="IsdocDocument"
                      control={<Radio />}
                      label={t('fileKinds.IsdocDocument')}
                    />
                  </RadioGroup>
                  <Typography variant="caption" color="text.secondary">
                    {t('files.upload.formatNote')}
                  </Typography>
                </FormControl>

                {uploadKind === 'IsdocDocument' && (
                  <Alert severity="info">{t('operations.send.isdocNote')}</Alert>
                )}

                <Button variant="outlined" component="label">
                  {uploadAttachments.length > 0
                    ? t('operations.send.uploadAttachmentsSelected', { count: uploadAttachments.length })
                    : t('operations.send.uploadAttachments')}
                  <input
                    type="file"
                    hidden
                    multiple
                    onChange={(event) => setUploadAttachments([...(event.target.files ?? [])])}
                  />
                </Button>
                {uploadAttachments.length > 0 && (
                  <Typography variant="caption" color="text.secondary">
                    {uploadAttachments.map((file) => file.name).join(', ')}
                  </Typography>
                )}
                <Typography variant="caption" color="text.secondary">
                  {t('operations.send.attachmentsHelp')}
                </Typography>
              </>
            )}

            <TextField
              label={t('operations.send.externalDocumentId')}
              value={externalDocumentId}
              onChange={(event) => setExternalDocumentId(event.target.value)}
              helperText={t('operations.send.externalDocumentIdHelp')}
              slotProps={{
                htmlInput: { maxLength: externalDocumentIdMaxLength, style: { fontFamily: 'monospace' } },
              }}
              size="small"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={mutation.isPending}>
            {t('actions.cancel')}
          </Button>
          <Button variant="contained" onClick={() => mutation.mutate()} disabled={!canSend}>
            {t('operations.send.send')}
          </Button>
        </DialogActions>
      </Dialog>

      {findings && (
        <ValidationFindingsDialog
          findings={findings}
          onClose={() => {
            const done = findings.severity === 'warning';
            setFindings(null);
            if (done) {
              onClose();
              if (operationAfterFindings) void navigate(`/operations/${operationAfterFindings}`);
            }
          }}
        />
      )}
    </>
  );
}
